import React from 'react';
import { Divider, styled } from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Dialog,
  TextV2,
  SvgCheckCircle1Bold,
  SvgRemoveCircleBold,
} from '@insights-ltd/design-library/components';
import { Link as RouterLink } from 'react-router';
import Link from '@mui/material/Link';
import { IDialects } from 'variables';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';

type BulkJobResult = {
  dialect: keyof IDialects;
  success: boolean;
  jobId: string;
};

type BulkJobStatusModalProps = {
  open: boolean;
  onClose: () => void;
  results: BulkJobResult[];
};

const StyledSvgCheckCircle1BoldSuccess = styled(SvgCheckCircle1Bold)(
  ({ theme }) => ({
    marginTop: theme.spacing(spacingSizeMap.XS),
    color: theme.palette.success.main,
  }),
);

const StyledSvgCheckCircle1BoldBlue = styled(SvgCheckCircle1Bold)(
  ({ theme }) => ({
    marginTop: theme.spacing(spacingSizeMap.XS),
    color: theme.palette.blue.main,
  }),
);

const StyledSvgRemoveCircleBold = styled(SvgRemoveCircleBold)(({ theme }) => ({
  color: theme.palette.error.main,
}));

const StatusRow = ({
  error,
  dialect,
  jobId,
}: {
  error: boolean;
  dialect: keyof IDialects;
  jobId?: string;
}) => {
  const errorState = () => {
    if (!jobId) {
      return (
        <Grid container spacing={1}>
          <Grid>
            <StyledSvgRemoveCircleBold />
          </Grid>
          <Grid>
            <TextV2 variant="bodyBold" component="p">
              Job not created
            </TextV2>
          </Grid>
        </Grid>
      );
    }
    return (
      <Grid container spacing={2}>
        <Grid>
          {!error ? (
            <StyledSvgCheckCircle1BoldSuccess />
          ) : (
            <StyledSvgCheckCircle1BoldBlue />
          )}
        </Grid>
        <Grid>
          <TextV2 component="p" variant="bodyBold">
            {!error ? 'Job created' : 'Job created with no strings'}
          </TextV2>
        </Grid>
      </Grid>
    );
  };

  return (
    <Grid container>
      <Grid
        container
        size={12}
        sx={{
          justifyContent: 'space-between',
        }}
      >
        <Grid>
          <TextV2 color="error" variant="bodyBold">
            <Link target="_blank" component={RouterLink} to={`/jobs/${jobId}`}>
              {jobId}
            </Link>
          </TextV2>
        </Grid>
        <Grid>
          <TextV2 component="p" color="textSecondary" variant="bodyBold">
            {dialect}
          </TextV2>
        </Grid>
        <Grid
          sx={{
            width: 1000,
          }}
        >
          <>{errorState()}</>
        </Grid>
      </Grid>
      <Grid size={12}>
        <Divider
          sx={(theme) => ({ marginTop: theme.spacing(spacingSizeMap.XS) })}
        />
      </Grid>
    </Grid>
  );
};

const BulkJobsStatusModal = ({
  open,
  onClose,
  results,
}: BulkJobStatusModalProps) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Error creating jobs"
      titleProps={{
        id: 'dialog-title',
      }}
      primaryButtonText="Okay"
      content={
        <Grid container spacing={2}>
          <Grid>
            <TextV2 component="p" color="textSecondary">
              Job summary
            </TextV2>
          </Grid>
          {results.map(({ dialect, success, jobId }) => (
            <Grid key={dialect} size={12}>
              <StatusRow error={!success} dialect={dialect} jobId={jobId} />
            </Grid>
          ))}
        </Grid>
      }
    />
  );
};

export default BulkJobsStatusModal;
